import { useDimensions } from "@repo/core/hooks/useDimensions";
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { Text } from "@repo/core/components/core/Text";
import { convertDataRows } from "@repo/core/components/core/DataTable/utils";
import { TableConvertedData } from "@repo/core/components/core/DataTable/types";
import { exportTanstackTableToCSV } from "@repo/core/utils/csv.utils";
import { useMemo, useRef } from "react";
import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import { format } from "date-fns";
import { ReportsResource } from "@repo/core/services/resources/reports";
import { newApInstance } from "@repo/core/services/instances/new-api";
import { useQuery } from "@tanstack/react-query";
import { useDeviceType } from "@repo/core/hooks/useDeviceType";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { useReportsFilters } from "@repo/core/hooks/useReportsFilters";
import { ROUTES } from "@repo/core/routes";

const reportsResource = new ReportsResource(newApInstance);

export const AnalysedProposalsController = () => {
  const { isMobile } = useDimensions();
  const { isMobile: isMobileDevice } = useDeviceType();
  const { push } = useNavigation();

  const {
    selectedAttendant,
    selectedProduct,
    selectedStore,
    filterStartDate,
    filterEndDate,
    onChangeStartDate,
    onChangeEndDate
  } = useReportsFilters();

  const filterModalRef = useRef<IModalRefProps>(null);

  const {
    data: analysedProposalsResponse,
    isFetching: isFetchingAnalysedProposals
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      "analysedProposals",
      filterStartDate,
      filterEndDate,
      selectedStore,
      selectedProduct,
      selectedAttendant
    ],
    queryFn: () =>
      reportsResource.getAnalysedProposalsReports({
        startDate: format(filterStartDate, "yyyy-MM-dd"),
        endDate: format(filterEndDate, "yyyy-MM-dd"),
        store: selectedStore,
        product: selectedProduct,
        attendant: selectedAttendant
      })
  });

  const columnHelper = createColumnHelper();

  const headerColumns = analysedProposalsResponse?.data?.table.headers.map(
    (header) => {
      return columnHelper.accessor(header.key, {
        header: () => (
          <Text
            fontSize="$xs"
            color={header?.styles?.text?.color || "$textLight700"}
            fontWeight={header?.styles?.text?.fontWeight || 600}
          >
            {header.label}
          </Text>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        meta: header?.styles
      });
    }
  );

  const { tableData, chartBarData, chartLineData, cardsData, hasReportData } =
    useMemo(() => {
      const tableData = convertDataRows(
        analysedProposalsResponse?.data?.table.headers,
        analysedProposalsResponse?.data?.table.rows
      );

      const chartBarData = analysedProposalsResponse?.data?.charts?.find(
        (chart) => chart.type === "bar"
      );

      const chartLineData = analysedProposalsResponse?.data?.charts?.find(
        (chart) => chart.type === "line"
      );

      const cardsData = analysedProposalsResponse?.data?.mobileTable;

      const hasReportData =
        Array.isArray(analysedProposalsResponse?.data?.table.rows) &&
        analysedProposalsResponse.data.table.rows.length > 0;

      return {
        tableData,
        chartBarData,
        chartLineData,
        cardsData,
        hasReportData
      };
    }, [analysedProposalsResponse?.data]);

  const table = useReactTable({
    data: tableData,
    columns: headerColumns as AccessorKeyColumnDef<TableConvertedData>[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel()
  });

  const topRightText = useMemo(() => {
    return `Atualizado em ${format(new Date(), "dd/MM")} às ${format(new Date(), "HH:mm:ss")}`;
  }, []);

  const handleExportTable = () => {
    exportTanstackTableToCSV(
      `Relatório_Propostas_Análisadas_${filterStartDate}_${filterEndDate}`,
      analysedProposalsResponse?.data?.table.headers,
      table
    );
  };

  const handleSelectFilters = () => {
    if (isMobileDevice) {
      push(ROUTES.reportSelectFilters);
    } else {
      if (filterModalRef.current) {
        filterModalRef.current.open();
      }
    }
  };
  return {
    isMobile,
    onChangeStartDate,
    onChangeEndDate,
    table,
    handleExportTable,
    selectedStore,
    selectedProduct,
    selectedAttendant,
    filterModalRef,
    topRightText,
    isFetchingAnalysedProposals,
    chartBarData,
    chartLineData,
    cardsData,
    filterStartDate,
    filterEndDate,
    hasReportData,
    handleSelectFilters
  };
};
